import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import authService from '../services/authService';
import tradesService from '../services/tradesService';
import CompanyTradeReportsPanel from '../components/CompanyTradeReportsPanel';
import EmployeeTradeCheckPanel from '../components/EmployeeTradeCheckPanel';
import TradeEntryModal from '../components/TradeEntryModal';
import SavedAccountsModal from '../components/SavedAccountsModal';
import TradeTrackerFilters from '../components/TradeTrackerFilters';
import { filterAndSortTrades, getTradeContractMultiplier } from './tradeTableUtils';
import { findImportAccountMatches } from './importAccountUtils';

const NEW_ACCOUNT_OPTION = '__new__';

const EMPTY_ACCOUNT_PARTS = {
  brokerage: '',
  accountName: '',
  accountNumber: '',
};

const safeTrim = (value) => String(value ?? '').trim();

const buildEmptyForm = () => ({
  symbol: '',
  action: 'BUY',
  quantity: '',
  amount: '',
  price: '',
  tradeDatetime: toDateTimeLocal(),
  brokerage: '',
  accountName: '',
  accountNumber: '',
});

const toDateTimeLocal = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const minute = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const formatDateTime = (isoValue) => {
  if (!isoValue) return 'N/A';
  const dt = new Date(isoValue);
  if (Number.isNaN(dt.getTime())) return isoValue;
  return dt.toLocaleString();
};

const toDateTimeInputValue = (isoValue) => {
  if (!isoValue) return toDateTimeLocal();
  const dt = new Date(isoValue);
  if (Number.isNaN(dt.getTime())) return toDateTimeLocal();
  const offset = dt.getTimezoneOffset() * 60000;
  return new Date(dt.getTime() - offset).toISOString().slice(0, 16);
};

const formatMoney = (value) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatNumber = (value, maxFraction = 6) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFraction,
  });
};

const getActionBadgeClass = (action) => {
  if (action === 'BUY') return 'bg-success';
  if (action === 'SELL') return 'bg-danger';
  return 'bg-info text-dark';
};

const buildAccountDisplayLabel = (brokerage, accountName, accountNumber) => {
  return [brokerage, accountName, accountNumber].filter(Boolean).join(' - ');
};

const getImportAccountPreviews = (preview) => {
  return Array.isArray(preview?.accounts) && preview.accounts.length > 0
    ? preview.accounts
    : [];
};

const getImportAccountPreview = (preview, accountIndex) => {
  const accountPreviews = getImportAccountPreviews(preview);
  return accountPreviews[accountIndex] || preview;
};

const toImportAccountForm = (preview) => ({
  brokerage: preview?.suggested_account?.brokerage || preview?.extracted_brokerage || '',
  accountName: preview?.suggested_account?.account_name || preview?.extracted_account_name || '',
  accountNumber: preview?.suggested_account?.account_number || preview?.extracted_account_number || '',
});

export {
  filterAndSortTrades,
  isCallOption,
  isDividendReinvestment,
  parseOptionSecurity,
} from './tradeTableUtils';

export default function TradeTrackerPage() {
  const auth = useContext(AuthContext);

  const [trades, setTrades] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [symbols, setSymbols] = useState([]);

  const [selectedAccountLabel, setSelectedAccountLabel] = useState('');

  const [form, setForm] = useState(buildEmptyForm);
  const [editingTradeId, setEditingTradeId] = useState('');

  const [importFiles, setImportFiles] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [importJobId, setImportJobId] = useState(null);
  const [importFileIndex, setImportFileIndex] = useState(0);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importCompleted, setImportCompleted] = useState(false);
  const [importError, setImportError] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [confirmedImportAccount, setConfirmedImportAccount] = useState(EMPTY_ACCOUNT_PARTS);
  const [selectedImportAccountLabel, setSelectedImportAccountLabel] = useState(NEW_ACCOUNT_OPTION);
  const [importAccountIndex, setImportAccountIndex] = useState(0);
  const [showImportStatementModal, setShowImportStatementModal] = useState(false);
  const [showTradeEntryModal, setShowTradeEntryModal] = useState(false);
  const [showSavedAccountsModal, setShowSavedAccountsModal] = useState(false);
  const [deletingTradeId, setDeletingTradeId] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    action: 'all',
    source: 'all',
    brokerage: 'all',
    symbol: 'all',
    coveredCalls: 'all',
    dividendReinvestments: 'all',
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'trade_datetime',
    direction: 'desc',
  });
  const [adminSelectedUserId, setAdminSelectedUserId] = useState('');
  const [appliedAdminUserId, setAppliedAdminUserId] = useState('');
  const [adminUserOptions, setAdminUserOptions] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersError, setAdminUsersError] = useState('');
  const [manualInputMode, setManualInputMode] = useState('quantity');

  const currentUserId = useMemo(() => {
    return safeTrim(auth?.user?.user_id || auth?.user?.username || auth?.user?.email);
  }, [auth?.user?.email, auth?.user?.user_id, auth?.user?.username]);

  const isTradeTrackerAdmin = Boolean(auth?.hasPermission?.('trade_tracker_admin'));

  const effectiveTargetUserId = useMemo(() => {
    if (!isTradeTrackerAdmin) return null;
    const candidate = safeTrim(appliedAdminUserId);
    // Treat viewing own data the same as no target — backend resolves identically
    if (!candidate || candidate === currentUserId) return null;
    return candidate;
  }, [appliedAdminUserId, currentUserId, isTradeTrackerAdmin]);

  const isViewingAnotherUser = useMemo(() => {
    if (!isTradeTrackerAdmin) {
      return false;
    }
    if (!effectiveTargetUserId) {
      return false;
    }
    if (!currentUserId) {
      return true;
    }
    return effectiveTargetUserId !== currentUserId;
  }, [currentUserId, effectiveTargetUserId, isTradeTrackerAdmin]);

  useEffect(() => {
    let cancelled = false;

    const loadAdminUserOptions = async () => {
      if (!isTradeTrackerAdmin) {
        return;
      }

      setAdminUsersLoading(true);
      setAdminUsersError('');
      try {
        const token = localStorage.getItem('token');
        const response = token ? await authService.listTradeTrackerUsers(token) : { users: [] };
        const users = Array.isArray(response?.users) ? response.users : [];
        const deduped = new Map();

        users.forEach((user) => {
          const userId = safeTrim(user?.user_id || user?.username || user?.email);
          if (!userId || deduped.has(userId)) {
            return;
          }
          const displayName = safeTrim(user?.full_name || user?.email || user?.username || userId);
          const email = safeTrim(user?.email);
          const label = email && email !== displayName
            ? `${displayName} (${email})`
            : displayName;
          deduped.set(userId, { value: userId, label });
        });

        if (currentUserId && !deduped.has(currentUserId)) {
          deduped.set(currentUserId, {
            value: currentUserId,
            label: `${currentUserId} (You)`,
          });
        }

        const nextOptions = Array.from(deduped.values()).sort((a, b) => (
          a.label.localeCompare(b.label)
        ));

        if (!cancelled) {
          setAdminUserOptions(nextOptions);
          if (currentUserId) {
            if (!safeTrim(adminSelectedUserId)) {
              setAdminSelectedUserId(currentUserId);
            }
            if (!safeTrim(appliedAdminUserId)) {
              setAppliedAdminUserId(currentUserId);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load admin user options:', err);
        if (!cancelled) {
          const fallbackOptions = currentUserId
            ? [{ value: currentUserId, label: `${currentUserId} (You)` }]
            : [];
          setAdminUserOptions(fallbackOptions);
          setAdminUsersError('Could not load user list. Showing your account only.');
          if (currentUserId) {
            if (!safeTrim(adminSelectedUserId)) {
              setAdminSelectedUserId(currentUserId);
            }
            if (!safeTrim(appliedAdminUserId)) {
              setAppliedAdminUserId(currentUserId);
            }
          }
        }
      } finally {
        if (!cancelled) {
          setAdminUsersLoading(false);
        }
      }
    };

    loadAdminUserOptions();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, isTradeTrackerAdmin]);

  const resolvedAccount = useMemo(() => {
    const selected = accounts.find((account) => account.display_label === selectedAccountLabel);
    return {
      brokerage: selected?.brokerage || safeTrim(form.brokerage),
      accountName: selected?.account_name || safeTrim(form.accountName),
      accountNumber: selected?.account_number || safeTrim(form.accountNumber),
    };
  }, [accounts, form.accountName, form.accountNumber, form.brokerage, selectedAccountLabel]);

  const loadTrades = useCallback(async () => {
    const response = await tradesService.getTrades(300, effectiveTargetUserId);
    setTrades(response.trades || []);
  }, [effectiveTargetUserId]);

  const loadOptions = useCallback(async () => {
    const response = await tradesService.getTradeOptions(1500, effectiveTargetUserId);
    const nextAccounts = response.accounts || [];
    const nextSymbols = response.symbols || [];
    setAccounts(nextAccounts);
    setSymbols(nextSymbols);
    return nextAccounts;
  }, [effectiveTargetUserId]);

  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountLabel) {
      setSelectedAccountLabel(accounts[0].display_label);
    }
  }, [accounts, selectedAccountLabel]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadTrades(), loadOptions()]);
    } catch (err) {
      console.error('Failed to load trades:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load trades.');
    } finally {
      setLoading(false);
    }
  }, [loadOptions, loadTrades]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadTrades().catch((err) => {
        console.error('Trade auto-refresh failed:', err);
      });
    }, 30000);

    return () => clearInterval(timer);
  }, [loadTrades]);

  useEffect(() => {
    if (!status || saving || status === 'Saving trade...' || status === 'Deleting trade...') {
      return undefined;
    }

    const timer = setTimeout(() => setStatus(''), 10000);
    return () => clearTimeout(timer);
  }, [saving, status]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetFormAfterSave = () => {
    setForm(buildEmptyForm());
    setEditingTradeId('');
    setSelectedAccountLabel('');
    setManualInputMode('quantity');
  };

  const closeTradeEntryModal = useCallback(() => {
    resetFormAfterSave();
    setShowTradeEntryModal(false);
    setError('');
    setStatus('');
  }, []);

  const openNewTradeModal = useCallback(() => {
    resetFormAfterSave();
    setError('');
    setStatus('');
    setShowTradeEntryModal(true);
  }, []);

  const startEditingTrade = (trade) => {
    setEditingTradeId(trade.trade_id);
    const tradeLabel = buildAccountDisplayLabel(
      trade.brokerage,
      trade.account_name,
      trade.account_number,
    );
    if (accounts.some((account) => account.display_label === tradeLabel)) {
      setSelectedAccountLabel(tradeLabel);
    } else {
      setSelectedAccountLabel('');
    }
    setForm({
      symbol: trade.symbol || '',
      action: trade.action || 'BUY',
      quantity: trade.quantity == null ? '' : String(trade.quantity),
      amount:
        trade.quantity == null || trade.price == null
          ? ''
          : String((Number(trade.quantity) * Number(trade.price) * getTradeContractMultiplier(trade.symbol)).toFixed(2)),
      price: trade.price == null ? '' : String(trade.price),
      tradeDatetime: toDateTimeInputValue(trade.trade_datetime),
      brokerage: trade.brokerage || '',
      accountName: trade.account_name || '',
      accountNumber: trade.account_number || '',
    });
    setManualInputMode('quantity');
    setError('');
    setStatus('');
    setShowTradeEntryModal(true);
  };

  const cancelEditing = () => {
    closeTradeEntryModal();
  };

  const derivedQuantity = useMemo(() => {
    if (manualInputMode === 'amount' && getTradeContractMultiplier(form.symbol) === 1) {
      const amount = Number(form.amount);
      const price = Number(form.price);
      if (!(amount > 0) || !(price > 0)) {
        return 0;
      }
      return Number((amount / price).toFixed(6));
    }
    return Number(form.quantity);
  }, [form.amount, form.price, form.quantity, form.symbol, manualInputMode]);

  const canSubmit = !saving &&
    safeTrim(form.symbol) &&
    resolvedAccount.brokerage &&
    resolvedAccount.accountName &&
    resolvedAccount.accountNumber &&
    form.tradeDatetime &&
    derivedQuantity > 0 &&
    Number(form.price) > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || isViewingAnotherUser) return;

    setSaving(true);
    setError('');
    setStatus('Saving trade...');

    try {
      const payload = {
        symbol: safeTrim(form.symbol).toUpperCase(),
        action: form.action,
        quantity: derivedQuantity,
        price: Number(form.price),
        trade_datetime: new Date(form.tradeDatetime).toISOString(),
        brokerage: resolvedAccount.brokerage,
        account_name: resolvedAccount.accountName,
        account_number: resolvedAccount.accountNumber,
      };

      const result = editingTradeId
        ? await tradesService.updateTrade(editingTradeId, payload)
        : await tradesService.createTrade(payload);
      const createdTrade = result?.trade;

      if (createdTrade) {
        setTrades((prev) => {
          if (editingTradeId) {
            return prev.map((trade) => (
              trade.trade_id === createdTrade.trade_id ? createdTrade : trade
            ));
          }
          return [createdTrade, ...prev];
        });
      }

      // Refresh options so new account/symbol values become reusable dropdowns.
      await loadOptions();
      resetFormAfterSave();
      setShowTradeEntryModal(false);
      setStatus(editingTradeId ? 'Trade updated.' : 'Trade saved.');
    } catch (err) {
      console.error('Failed to save trade:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save trade.');
      setStatus('');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTrade = async (trade) => {
    if (isViewingAnotherUser || saving || deletingTradeId) return;

    const confirmed = window.confirm(
      `Delete ${trade.action} ${trade.symbol} from ${formatDateTime(trade.trade_datetime)}?`,
    );
    if (!confirmed) return;

    setDeletingTradeId(trade.trade_id);
    setError('');
    setStatus('Deleting trade...');

    try {
      await tradesService.deleteTrade(trade.trade_id);
      setTrades((prev) => prev.filter((row) => row.trade_id !== trade.trade_id));

      if (editingTradeId === trade.trade_id) {
        resetFormAfterSave();
        setShowTradeEntryModal(false);
      }

      setStatus('Trade deleted.');
    } catch (err) {
      console.error('Failed to delete trade:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to delete trade.');
      setStatus('');
    } finally {
      setDeletingTradeId('');
    }
  };

  const resetImportStatementModal = useCallback(() => {
    setImportFiles([]);
    setImportFile(null);
    setImportJobId(null);
    setImportFileIndex(0);
    setImportPreview(null);
    setImportCompleted(false);
    setConfirmedImportAccount(EMPTY_ACCOUNT_PARTS);
    setSelectedImportAccountLabel(NEW_ACCOUNT_OPTION);
    setImportAccountIndex(0);
    setImportError('');
    setImportStatus('');
  }, []);

  const openImportStatementModal = useCallback(() => {
    resetImportStatementModal();
    setShowImportStatementModal(true);
  }, [resetImportStatementModal]);

  const closeImportStatementModal = useCallback(() => {
    resetImportStatementModal();
    setShowImportStatementModal(false);
  }, [resetImportStatementModal]);

  const canPreviewImport = !importing && importFile;
  const importAccountPreviews = useMemo(() => getImportAccountPreviews(importPreview), [importPreview]);
  const importAccountCount = importPreview
    ? Number(importPreview.account_count || importAccountPreviews.length || 1)
    : 0;
  const activeImportPreview = useMemo(() => (
    getImportAccountPreview(importPreview, importAccountIndex)
  ), [importAccountIndex, importPreview]);
  const canApplyImport = !importing && importJobId &&
    safeTrim(confirmedImportAccount?.brokerage) &&
    safeTrim(confirmedImportAccount?.accountName) &&
    safeTrim(confirmedImportAccount?.accountNumber) &&
    activeImportPreview;

  const detectedImportAccount = useMemo(() => (
    toImportAccountForm(activeImportPreview)
  ), [activeImportPreview]);

  const importAccountMatches = useMemo(() => (
    findImportAccountMatches(detectedImportAccount.accountNumber, accounts)
  ), [accounts, detectedImportAccount.accountNumber]);

  const previewWarnings = useMemo(() => {
    const warnings = Array.isArray(activeImportPreview?.warnings) ? activeImportPreview.warnings : [];
    const detectedBroker = safeTrim(activeImportPreview?.extracted_brokerage || activeImportPreview?.source_broker || '');
    return warnings.filter((warning) => {
      if (!warning) return false;
      const message = String(warning).trim();
      if (detectedBroker && message.toLowerCase().includes('broker could not be identified')) {
        return false;
      }
      return true;
    });
  }, [activeImportPreview]);

  const findMatchingAccountLabel = useCallback((candidate, accountList = accounts) => {
    const matches = findImportAccountMatches(candidate?.accountNumber, accountList);
    return matches.length === 1 ? matches[0].display_label : NEW_ACCOUNT_OPTION;
  }, [accounts]);

  const prepareImportAccountSelection = useCallback((preview, accountIndex, accountList = accounts) => {
    const accountPreview = getImportAccountPreview(preview, accountIndex);
    const nextConfirmedAccount = toImportAccountForm(accountPreview);
    const matchedLabel = findMatchingAccountLabel(nextConfirmedAccount, accountList);
    setSelectedImportAccountLabel(matchedLabel);

    if (matchedLabel !== NEW_ACCOUNT_OPTION) {
      const matchedAccount = accountList.find((account) => account.display_label === matchedLabel);
      if (matchedAccount) {
        setConfirmedImportAccount({
          brokerage: matchedAccount.brokerage || '',
          accountName: matchedAccount.account_name || '',
          accountNumber: matchedAccount.account_number || '',
        });
        return;
      }
    }

    setConfirmedImportAccount(nextConfirmedAccount);
  }, [accounts, findMatchingAccountLabel]);

  const handlePreviewImport = async () => {
    if (!canPreviewImport || isViewingAnotherUser) return;
    setImporting(true);
    setImportCompleted(false);
    setImportError('');
    setImportStatus('Extracting trades from statement. Large statements can take a minute...');
    try {
      const jobId = await tradesService.extractStatement(importFile);
      setImportJobId(jobId);
      setImportStatus('Previewing statement import...');
      const preview = await tradesService.importStatement({
        jobId,
        dryRun: true,
      });
      setImportAccountIndex(0);
      setImportPreview(preview);
      prepareImportAccountSelection(preview, 0);
      const accountCount = Number(preview.account_count || getImportAccountPreviews(preview).length || 1);
      let previewStatus = 'Preview ready. Review duplicates and new rows below.';
      if (importFiles.length > 1) {
        previewStatus = `Preview ready for file ${importFileIndex + 1} of ${importFiles.length}. Review account 1 of ${accountCount}.`;
      } else if (accountCount > 1) {
        previewStatus = `Preview ready. Review account 1 of ${accountCount}.`;
      }
      setImportStatus(previewStatus);
    } catch (err) {
      console.error('Failed to preview statement import:', err);
      setImportError(err.response?.data?.detail || err.message || 'Failed to preview statement import.');
      setImportStatus('');
    } finally {
      setImporting(false);
    }
  };

  const handleApplyImport = async () => {
    if (!canApplyImport || isViewingAnotherUser) return;
    setImporting(true);
    setImportCompleted(false);
    setImportError('');
    setImportStatus('Importing missing trades from statement...');
    try {
      const result = await tradesService.importStatement({
        jobId: importJobId,
        brokerage: safeTrim(confirmedImportAccount?.brokerage),
        account_name: safeTrim(confirmedImportAccount?.accountName),
        account_number: safeTrim(confirmedImportAccount?.accountNumber),
        account_index: importAccountIndex,
        dryRun: false,
      });
      setImportPreview(result);
      const hybridizedCount = Number(result.hybridized_count || 0);
      const hybridizedText =
        hybridizedCount > 0 ? ` and hybridized ${hybridizedCount} trades` : '';
      const [, refreshedAccounts] = await Promise.all([loadTrades(), loadOptions()]);
      const accountCount = Number(result.account_count || getImportAccountPreviews(result).length || 1);
      const nextAccountIndex = importAccountIndex + 1;
      if (nextAccountIndex < accountCount) {
        setImportAccountIndex(nextAccountIndex);
        prepareImportAccountSelection(result, nextAccountIndex, refreshedAccounts);
        setImportCompleted(false);
        setImportStatus(
          `${importFiles.length > 1 ? `Imported file ${importFileIndex + 1} of ${importFiles.length}, ` : ''}account ${importAccountIndex + 1} of ${accountCount}: ${result.imported_count} trades${hybridizedText} and skipped ${result.duplicate_count} duplicates. Review account ${nextAccountIndex + 1} next.`,
        );
      } else if (importFileIndex + 1 < importFiles.length) {
        const nextFileIndex = importFileIndex + 1;
        const nextFile = importFiles[nextFileIndex];
        setImportFileIndex(nextFileIndex);
        setImportFile(nextFile);
        setImportJobId(null);
        setImportPreview(null);
        setImportAccountIndex(0);
        setConfirmedImportAccount(EMPTY_ACCOUNT_PARTS);
        setSelectedImportAccountLabel(NEW_ACCOUNT_OPTION);
        setImportCompleted(false);
        const importedAccountSummary =
          `Imported account ${importAccountIndex + 1} of ${accountCount} in file ${importFileIndex + 1} of ${importFiles.length}: ${result.imported_count} trades${hybridizedText} and skipped ${result.duplicate_count} duplicates.`;
        setImportStatus(
          `${importedAccountSummary} Extracting file ${nextFileIndex + 1} of ${importFiles.length}...`,
        );
        try {
          const nextJobId = await tradesService.extractStatement(nextFile);
          setImportJobId(nextJobId);
          const nextPreview = await tradesService.importStatement({
            jobId: nextJobId,
            dryRun: true,
          });
          setImportPreview(nextPreview);
          prepareImportAccountSelection(nextPreview, 0, refreshedAccounts);
          const nextAccountCount = Number(
            nextPreview.account_count || getImportAccountPreviews(nextPreview).length || 1,
          );
          setImportStatus(
            `${importedAccountSummary} Preview ready for file ${nextFileIndex + 1} of ${importFiles.length}. Review account 1 of ${nextAccountCount}.`,
          );
        } catch (previewError) {
          console.error('Failed to preview next statement file:', previewError);
          setImportError(
            previewError.response?.data?.detail ||
              previewError.message ||
              'Failed to preview next statement file.',
          );
          setImportStatus(importedAccountSummary);
        }
      } else {
        setImportCompleted(true);
        setImportStatus(
          `${importFiles.length > 1 ? `Imported file ${importFileIndex + 1} of ${importFiles.length}, ` : ''}account ${importAccountIndex + 1} of ${accountCount}: ${result.imported_count} trades${hybridizedText} and skipped ${result.duplicate_count} duplicates.`,
        );
      }
    } catch (err) {
      console.error('Failed to import statement:', err);
      setImportError(err.response?.data?.detail || err.message || 'Failed to import statement.');
      setImportCompleted(false);
      setImportStatus('');
    } finally {
      setImporting(false);
    }
  };

  const getImportStatusBadgeClass = (statusValue) => {
    if (statusValue === 'duplicate') return 'bg-secondary';
    if (statusValue === 'imported') return 'bg-primary';
    if (statusValue === 'hybridized') return 'bg-info text-dark';
    return 'bg-success';
  };

  const uniqueBrokerages = useMemo(() => {
    return Array.from(new Set((trades || []).map((trade) => trade.brokerage).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [trades]);

  const uniqueSymbols = useMemo(() => {
    return Array.from(new Set((trades || []).map((trade) => trade.symbol).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [trades]);

  const filteredAndSortedTrades = useMemo(() => {
    return filterAndSortTrades(trades, filters, sortConfig);
  }, [filters, sortConfig, trades]);


  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return <span className="text-muted">↕</span>;
    return sortConfig.direction === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">
          <i className="bi bi-graph-up-arrow me-2"></i>
          Trade Tracker
        </h2>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={loadAll}
          disabled={loading || saving}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>


      {isTradeTrackerAdmin && <CompanyTradeReportsPanel />}

      {isTradeTrackerAdmin && (
        <div className="mb-3">
          <EmployeeTradeCheckPanel />
        </div>
      )}

      {isViewingAnotherUser && (
        <div className="alert alert-warning d-flex align-items-center gap-2">
          <i className="bi bi-shield-lock-fill"></i>
          <span>Viewing another user's trades in read-only mode.</span>
        </div>
      )}

      <TradeEntryModal
        show={showTradeEntryModal}
        title={editingTradeId ? 'Edit Trade' : 'New Trade'}
        form={form}
        accounts={accounts}
        symbols={symbols}
        selectedAccountLabel={selectedAccountLabel}
        onSelectedAccountLabelChange={(value) => {
          setSelectedAccountLabel(value);
          const selected = accounts.find((account) => account.display_label === value);
          if (selected) {
            setForm((prev) => ({
              ...prev,
              brokerage: selected.brokerage,
              accountName: selected.account_name,
              accountNumber: selected.account_number,
            }));
          }
        }}
        onManageAccounts={() => setShowSavedAccountsModal(true)}
        onFieldChange={handleFieldChange}
        manualInputMode={manualInputMode}
        onManualInputModeChange={setManualInputMode}
        derivedQuantity={derivedQuantity}
        onSubmit={handleSubmit}
        onClose={closeTradeEntryModal}
        onCancelEdit={cancelEditing}
        canSubmit={canSubmit}
        saving={saving}
        isViewingAnotherUser={isViewingAnotherUser}
        editingTradeId={editingTradeId}
      />

      <SavedAccountsModal
        show={showSavedAccountsModal}
        onClose={() => setShowSavedAccountsModal(false)}
        onAccountsChanged={loadOptions}
      />

      {error && (
        <div className="alert alert-danger d-flex align-items-start gap-2">
          <i className="bi bi-exclamation-triangle-fill mt-1"></i>
          <span>{error}</span>
        </div>
      )}

      {status && (
        <div
          className="alert alert-info trade-status-toast d-flex align-items-center gap-2"
          role="status"
          aria-live="polite"
        >
          {saving ? (
            <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
          ) : (
            <i className="bi bi-check-circle-fill"></i>
          )}
          <span>{status}</span>
        </div>
      )}

      {showImportStatementModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closeImportStatementModal}
          />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) closeImportStatementModal(); }}
          >
            <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Broker Statement Import</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeImportStatementModal}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  {importError && (
                    <div className="alert alert-danger py-2">
                      {importError}
                    </div>
                  )}
                  {importStatus && (
                    <div className="alert alert-info py-2">
                      {importStatus}
                    </div>
                  )}

                  {!importPreview && (
                    <>
                      <div className="mb-3">
                        <label htmlFor="import-statement-files" className="form-label fw-semibold">Statement Files</label>
                        <input
                          id="import-statement-files"
                          type="file"
                          className="form-control"
                          accept="application/pdf,.pdf,text/csv,.csv"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setImportFiles(files);
                            setImportFile(files[0] || null);
                            setImportJobId(null);
                            setImportFileIndex(0);
                            setImportPreview(null);
                            setImportCompleted(false);
                            setConfirmedImportAccount(EMPTY_ACCOUNT_PARTS);
                            setSelectedImportAccountLabel(NEW_ACCOUNT_OPTION);
                            setImportAccountIndex(0);
                            setImportError('');
                            setImportStatus('');
                          }}
                          disabled={importing || isViewingAnotherUser}
                        />
                        <div className="form-text">Upload broker statement PDFs, Webull trade CSVs, Robinhood activity CSVs, or Fidelity history CSVs to fill gaps and double-check manual entries. Fidelity CSVs detect each account; for Webull and Robinhood CSVs, choose a saved account or enter the account name and number below.</div>
                        {importFiles.length > 0 && (
                          <div className="form-text">
                            {importFiles.length} file{importFiles.length === 1 ? '' : 's'} selected: {importFiles.map((file) => file.name).join(', ')}
                          </div>
                        )}
                      </div>

                      <div className="d-grid gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={handlePreviewImport}
                          disabled={!canPreviewImport || isViewingAnotherUser}
                        >
                          {importing ? 'Working...' : 'Preview Import'}
                        </button>
                      </div>
                    </>
                  )}

                  {importPreview && (
                    <div className="mt-3 small">
                      {importFiles.length > 1 && (
                        <div className="mb-2">
                          <span className="badge bg-primary-subtle text-primary-emphasis border">
                            File {importFileIndex + 1} of {importFiles.length}: {importFile?.name}
                          </span>
                        </div>
                      )}
                      {importAccountCount > 1 && (
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                          <span className="badge bg-primary-subtle text-primary-emphasis border">
                            Account {importAccountIndex + 1} of {importAccountCount}
                          </span>
                          <span className="text-muted">
                            {activeImportPreview?.suggested_account?.display_label || buildAccountDisplayLabel(
                              detectedImportAccount.brokerage,
                              detectedImportAccount.accountName,
                              detectedImportAccount.accountNumber,
                            )}
                          </span>
                        </div>
                      )}
                      <div className="border rounded mb-3" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                        <div className="table-responsive">
                          <table className="table table-sm table-striped mb-0 align-middle">
                            <thead className="table-light sticky-top">
                              <tr>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Symbol</th>
                                <th>Action</th>
                                <th className="text-end">Quantity</th>
                                <th className="text-end">Price</th>
                                <th>Note</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Array.isArray(activeImportPreview?.rows) && activeImportPreview.rows.length > 0 ? (
                                activeImportPreview.rows.map((row, index) => (
                                  <tr key={`${row.symbol}-${row.trade_date}-${index}`}>
                                    <td>
                                      <span className={`badge ${getImportStatusBadgeClass(row.status)}`}>
                                        {row.status}
                                      </span>
                                    </td>
                                    <td>{row.trade_date}</td>
                                    <td>{row.symbol}</td>
                                    <td>{row.action}</td>
                                    <td className="text-end">{formatNumber(row.quantity)}</td>
                                    <td className="text-end">{formatMoney(row.price)}</td>
                                    <td>{row.parse_note || '—'}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="7" className="text-muted text-center py-3">
                                    No trades were detected in this statement preview.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="mt-2">
                        <label htmlFor="import-saved-account" className="form-label fw-semibold mb-1">Saved Account</label>
                        <select
                          id="import-saved-account"
                          className="form-select"
                          value={selectedImportAccountLabel}
                          onChange={(e) => {
                            if (importing || isViewingAnotherUser) return;
                            const selectedLabel = e.target.value;
                            setSelectedImportAccountLabel(selectedLabel);
                            if (selectedLabel === NEW_ACCOUNT_OPTION) {
                              setConfirmedImportAccount((prev) => ({
                                brokerage: safeTrim(prev?.brokerage) || detectedImportAccount.brokerage,
                                accountName: safeTrim(prev?.accountName) || detectedImportAccount.accountName,
                                accountNumber: safeTrim(prev?.accountNumber) || detectedImportAccount.accountNumber,
                              }));
                              return;
                            }

                            const selected = accounts.find((account) => account.display_label === selectedLabel);
                            if (selected) {
                              setConfirmedImportAccount({
                                brokerage: selected.brokerage || '',
                                accountName: selected.account_name || '',
                                accountNumber: selected.account_number || '',
                              });
                            }
                          }}
                          disabled={importing || isViewingAnotherUser}
                        >
                          <option value={NEW_ACCOUNT_OPTION}>+ Add new account</option>
                          {accounts.map((account) => (
                            <option key={account.display_label} value={account.display_label}>
                              {account.display_label}
                            </option>
                          ))}
                        </select>
                        <div className="form-text">
                          Choose a saved account or keep Add new account and edit the fields below.
                        </div>
                        {importAccountMatches.length > 1 && selectedImportAccountLabel === NEW_ACCOUNT_OPTION && (
                          <div className="text-warning mt-2" role="alert">
                            Multiple saved accounts match the statement account number or its last four digits. Choose the correct saved account before importing.
                          </div>
                        )}

                        <div className="row g-3 mt-1">
                          <div className="col-md-4">
                            <label className="form-label fw-semibold mb-1">Confirm Brokerage</label>
                            <input
                              type="text"
                              className="form-control"
                              value={confirmedImportAccount.brokerage}
                              onChange={(e) => {
                                if (importing || isViewingAnotherUser) return;
                                setConfirmedImportAccount((prev) => ({ ...prev, brokerage: e.target.value }));
                              }}
                              placeholder="Confirm brokerage"
                              disabled={importing || isViewingAnotherUser}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label fw-semibold mb-1">Confirm Account Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={confirmedImportAccount.accountName}
                              onChange={(e) => {
                                if (importing || isViewingAnotherUser) return;
                                setConfirmedImportAccount((prev) => ({ ...prev, accountName: e.target.value }));
                              }}
                              placeholder="Confirm account name"
                              disabled={importing || isViewingAnotherUser}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label fw-semibold mb-1">Confirm Account Number</label>
                            <input
                              type="text"
                              className="form-control"
                              value={confirmedImportAccount.accountNumber}
                              onChange={(e) => {
                                if (importing || isViewingAnotherUser) return;
                                setConfirmedImportAccount((prev) => ({ ...prev, accountNumber: e.target.value }));
                              }}
                              placeholder="Confirm account number"
                              disabled={importing || isViewingAnotherUser}
                            />
                          </div>
                        </div>
                        <div className="form-text mt-2">
                          These values will be used for imported trades and duplicate matching.
                        </div>
                      </div>
                      {previewWarnings.length > 0 && (
                        <div className="text-warning mt-2">
                          {previewWarnings.join(' ')}
                        </div>
                      )}

                      <div className="d-grid gap-2 mt-4">
                        {importing ? (
                          <div className="progress" role="progressbar" aria-label="Importing missing trades" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100">
                            <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%' }}>
                              Importing...
                            </div>
                          </div>
                        ) : importCompleted ? (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={closeImportStatementModal}
                          >
                            Close
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleApplyImport}
                            disabled={!canApplyImport || isViewingAnotherUser}
                          >
                            {importAccountCount > 1
                              ? `Import Account ${importAccountIndex + 1} of ${importAccountCount}`
                              : 'Import Missing Trades'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {isTradeTrackerAdmin && (
        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <div className="d-flex align-items-center gap-2 flex-nowrap">
              <label htmlFor="admin-user-selector" className="form-label fw-semibold mb-0">Admin User Selector</label>
              <div className="flex-grow-1">
                <select
                  id="admin-user-selector"
                  className="form-select"
                  value={adminSelectedUserId}
                  onChange={(e) => setAdminSelectedUserId(e.target.value)}
                  disabled={adminUsersLoading}
                >
                  {adminUserOptions.map((userOption) => (
                    <option key={userOption.value} value={userOption.value}>
                      {userOption.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setAppliedAdminUserId(safeTrim(adminSelectedUserId))}
                disabled={!safeTrim(adminSelectedUserId) || safeTrim(adminSelectedUserId) === safeTrim(appliedAdminUserId)}
              >
                Load Trades
              </button>
            </div>
            {adminUsersError && (
              <div className="text-warning small mt-1">{adminUsersError}</div>
            )}
            {isViewingAnotherUser && (
              <div className="small text-muted mt-2">
                Viewing user: {effectiveTargetUserId}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="row g-3">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span className="fw-semibold">Recent Trades</span>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={openNewTradeModal}
                  disabled={isViewingAnotherUser}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  Add Trade
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={openImportStatementModal}
                  disabled={isViewingAnotherUser}
                >
                  Import Broker Statement
                </button>
                <span className="badge bg-secondary">{filteredAndSortedTrades.length}</span>
              </div>
            </div>
            <div className="card-body p-0">
              <TradeTrackerFilters
                filters={filters}
                setFilters={setFilters}
                uniqueBrokerages={uniqueBrokerages}
                uniqueSymbols={uniqueSymbols}
                onClear={() => setFilters({
                  search: '',
                  action: 'all',
                  source: 'all',
                  brokerage: 'all',
                  symbol: 'all',
                  coveredCalls: 'all',
                  dividendReinvestments: 'all',
                })}
              />
              {loading ? (
                <div className="p-4 text-center text-muted">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Loading trades...
                </div>
              ) : trades.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  No trades yet. Use Add Trade to create your first entry.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-striped mb-0 align-middle">
                    <thead className="table-dark">
                      <tr>
                        <th><button type="button" className="btn btn-link btn-sm p-0 text-white text-decoration-none" onClick={() => handleSort('trade_datetime')}>Time {renderSortIndicator('trade_datetime')}</button></th>
                        <th><button type="button" className="btn btn-link btn-sm p-0 text-white text-decoration-none" onClick={() => handleSort('brokerage')}>Brokerage {renderSortIndicator('brokerage')}</button></th>
                        <th><button type="button" className="btn btn-link btn-sm p-0 text-white text-decoration-none" onClick={() => handleSort('account_name')}>Account Name {renderSortIndicator('account_name')}</button></th>
                        <th><button type="button" className="btn btn-link btn-sm p-0 text-white text-decoration-none" onClick={() => handleSort('account_number')}>Account Number {renderSortIndicator('account_number')}</button></th>
                        <th><button type="button" className="btn btn-link btn-sm p-0 text-white text-decoration-none" onClick={() => handleSort('symbol')}>Symbol {renderSortIndicator('symbol')}</button></th>
                        <th><button type="button" className="btn btn-link btn-sm p-0 text-white text-decoration-none" onClick={() => handleSort('source')}>Source {renderSortIndicator('source')}</button></th>
                        <th><button type="button" className="btn btn-link btn-sm p-0 text-white text-decoration-none" onClick={() => handleSort('action')}>Action {renderSortIndicator('action')}</button></th>
                        <th className="text-end"><button type="button" className="btn btn-link btn-sm p-0 text-white text-decoration-none" onClick={() => handleSort('quantity')}>Quantity {renderSortIndicator('quantity')}</button></th>
                        <th className="text-end"><button type="button" className="btn btn-link btn-sm p-0 text-white text-decoration-none" onClick={() => handleSort('price')}>Price {renderSortIndicator('price')}</button></th>
                        <th className="text-end"><button type="button" className="btn btn-link btn-sm p-0 text-white text-decoration-none" onClick={() => handleSort('notional')}>Notional {renderSortIndicator('notional')}</button></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedTrades.map((trade) => (
                        <tr key={trade.trade_id}>
                          <td>{formatDateTime(trade.trade_datetime)}</td>
                          <td>{trade.brokerage}</td>
                          <td>{trade.account_name}</td>
                          <td>{trade.account_number}</td>
                          <td className="fw-semibold">{trade.symbol}</td>
                          <td>
                            {trade.hybridized ? (
                              <span className="badge bg-info-subtle text-info-emphasis border">Hybrid</span>
                            ) : trade.auto_processed ? (
                              <span className="badge bg-primary-subtle text-primary-emphasis border">Auto</span>
                            ) : (
                              <span className="badge bg-light text-dark border">Manual</span>
                            )}
                            {trade.time_estimated && (
                              <span className="badge bg-warning-subtle text-warning-emphasis border ms-1">Date Only</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${getActionBadgeClass(trade.action)}`}>
                              {trade.action}
                            </span>
                          </td>
                          <td className="text-end">{formatNumber(trade.quantity)}</td>
                          <td className="text-end">{formatMoney(trade.price)}</td>
                          <td className="text-end fw-semibold">{formatMoney(trade.notional)}</td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => startEditingTrade(trade)}
                                disabled={isViewingAnotherUser || saving || Boolean(deletingTradeId)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteTrade(trade)}
                                disabled={isViewingAnotherUser || saving || Boolean(deletingTradeId)}
                                aria-label="Delete trade"
                                title="Delete trade"
                              >
                                {deletingTradeId === trade.trade_id ? (
                                  <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                ) : (
                                  <i className="bi bi-trash"></i>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
